// src/Components/Admin/SupplierList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus, Search, X, Save, Building2, ChevronLeft,
  Users, Trash2, Edit2, ChevronDown,
  Palette, Loader2, ChevronRight, Eye, Mail, Phone,
  Globe, CreditCard, ShieldCheck,
  TrendingUp, Activity, MapPin, Tag
} from 'lucide-react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import './SalesOrder.css';
import { useLegacyTheme } from '../../hooks/useLegacyTheme';
import SupplierFormModal from './SupplierFormModal';
import ListCustomizer from './ListCustomizer';

/* ==================== UI COMPONENTS ==================== */
const StatusBadge = ({ isInactive, themeColor }) => (
  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide shadow-sm ${!isInactive ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
    {!isInactive ? 'Operational' : 'Restricted'}
  </span>
);

export default function SupplierList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const user_roles = useSelector((state) => state.user.user_roles || []);
  const warehouse = useSelector((state) => state.user.warehouse);
  const isAdmin = user_roles.includes("Administrator") || user_roles.includes("System Manager");
  const [customColumns, setCustomColumns] = useState(() => {
    const saved = localStorage.getItem('custom_columns_Supplier');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);


  // Theme Hook
  const { isGreen, themeColor, themeColorHover, themeLight, toggleTheme } = useLegacyTheme();

  // Filters
  const location = useLocation();
  const [filterSearch, setFilterSearch] = useState(location.state?.search || '');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [globalSearching, setGlobalSearching] = useState(false);

  // Global Sync Modal States
  const [showGlobalSyncModal, setShowGlobalSyncModal] = useState(false);
  const [globalSyncSearch, setGlobalSyncSearch] = useState('');
  const [globalSuppliers, setGlobalSuppliers] = useState([]);
  const [selectedGlobalSuppliers, setSelectedGlobalSuppliers] = useState([]);
  const [searchingGlobal, setSearchingGlobal] = useState(false);
  const [syncingGlobal, setSyncingGlobal] = useState(false);

  const openGlobalSyncModal = () => {
    setGlobalSyncSearch(filterSearch || '');
    setGlobalSuppliers([]);
    setSelectedGlobalSuppliers([]);
    setShowGlobalSyncModal(true);
    if (filterSearch) {
      setTimeout(() => {
        runGlobalSearch(filterSearch);
      }, 100);
    }
  };

  const runGlobalSearch = async (searchTermOverride) => {
    const q = searchTermOverride !== undefined ? searchTermOverride : globalSyncSearch;
    if (!q.trim()) return Swal.fire('Search', 'Please enter a name or code to discover.', 'info');

    setSearchingGlobal(true);
    try {
      const res = await axios.get('/api/method/future_retail_pos.api.supplier.find_supplier_globally_retail', {
        params: { search_term: q },
        withCredentials: true
      });
      const results = res.data.message?.data || [];
      setGlobalSuppliers(results);
      setSelectedGlobalSuppliers([]);
    } catch (err) {
      console.error('Global search failed:', err);
      Swal.fire('Search Failed', 'Unable to reach global registry.', 'error');
    } finally {
      setSearchingGlobal(false);
    }
  };

  const handleBulkSync = async () => {
    if (selectedGlobalSuppliers.length === 0) return;
    setSyncingGlobal(true);
    try {
      Swal.fire({ title: 'Synchronizing Partners...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const res = await axios.post('/api/method/future_retail_pos.api.supplier.bulk_enable_suppliers_for_branch', {
        suppliers: JSON.stringify(selectedGlobalSuppliers),
        warehouse: warehouse
      }, { withCredentials: true });

      if (res.data.message?.success) {
        Swal.fire({ icon: 'success', title: 'Sync Completed', text: 'All selected suppliers are now active for your branch.', timer: 2000 });
        setShowGlobalSyncModal(false);
        fetchSuppliers();
      } else {
        Swal.fire('Error', res.data.message?.message || 'Sync failed.', 'error');
      }
    } catch (err) {
      console.error('Bulk sync failed:', err);
      Swal.fire('Sync Error', err.response?.data?.message || 'Connection failure.', 'error');
    } finally {
      setSyncingGlobal(false);
    }
  };

  // Allow browser scroll
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
    };
  }, []);

  const [form, setForm] = useState({
    supplier_name: '',
    supplier_group: '',
    supplier_type: 'Company',
    disabled: false,
    tax_id: '',
    website: '',
    email_id: '',
    mobile_no: '',
    address: '',
    contact_person: '',
    currency: 'AED'
  });

  const [supplierGroups] = useState(['Distributor', 'Manufacturer', 'Service Provider', 'Wholesaler', 'Retailer']);

  /* ==================== FETCH DATA ==================== */
  useEffect(() => {
    fetchSuppliers();
  }, [customColumns]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/method/future_retail_pos.api.item.get_suppliers_list', {
        params: {
          warehouse: warehouse,
          limit: 1000,
          extra_fields: JSON.stringify(customColumns)
        },
        withCredentials: true
      });
      setSuppliers(Array.isArray(res.data.message?.data) ? res.data.message.data : []);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      Swal.fire({ icon: 'error', title: 'Connection Failure', text: 'System unable to synchronize with supplier database.', borderRadius: '2rem' });
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const fetchSupplierDetails = async (name) => {
    try {
      const res = await axios.get(`/api/resource/Supplier/${name}`, { withCredentials: true });
      const data = res.data.data;
      setForm({
        supplier_name: data.supplier_name || '',
        supplier_group: data.supplier_group || '',
        supplier_type: data.supplier_type || 'Company',
        disabled: data.disabled === 1,
        tax_id: data.tax_id || '',
        website: data.website || '',
        email_id: data.email_id || '',
        mobile_no: data.mobile_no || '',
        address: data.address || '',
        contact_person: data.contact_person || '',
        currency: data.currency || 'AED'
      });
    } catch (err) {
      console.error('Failed to load details:', err);
    }
  };

  /* ==================== FILTERING ==================== */
  const filteredSuppliers = useMemo(() => {
    return suppliers.filter(s => {
      const q = filterSearch.toLowerCase();
      const matchesSearch = !filterSearch ||
        s.supplier_name?.toLowerCase().includes(q) ||
        s.name?.toLowerCase().includes(q) ||
        s.email_id?.toLowerCase().includes(q) ||
        s.mobile_no?.includes(filterSearch);

      const matchesGroup = !filterGroup || s.supplier_group === filterGroup;
      const matchesType = !filterType || s.supplier_type === filterType;
      const matchesStatus = !filterStatus || (filterStatus === 'active' ? !s.disabled : s.disabled);

      return matchesSearch && matchesGroup && matchesType && matchesStatus;
    });
  }, [suppliers, filterSearch, filterGroup, filterType, filterStatus]);

  const checkSupplierGlobally = async () => {
    if (!filterSearch) {
      Swal.fire({ icon: 'info', title: 'Global Discovery', text: 'Please enter a supplier name or mobile number in the search box first.' });
      return;
    }
    setGlobalSearching(true);
    try {
      const res = await axios.get('/api/method/future_retail_pos.api.supplier.find_supplier_globally_retail', {
        params: { search_term: filterSearch },
        withCredentials: true
      });
      const results = res.data.message?.data || [];
      if (results.length > 0) {
        const html = `
          <div style="text-align: left; max-height: 400px; overflow-y: auto; padding: 10px;">
            ${results.map(s => `
              <div style="display: flex; gap: 12px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 8px; background: #fff;">
                <div style="width: 38px; height: 38px; background: #f1f5f9; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                  <b style="color: #4f46e5; font-size: 14px;">${s.supplier_name.charAt(0)}</b>
                </div>
                <div style="flex: 1;">
                  <div style="font-weight: 700; font-size: 14px; color: #1e293b;">${s.supplier_name}</div>
                  <div style="font-size: 11px; color: #64748b; font-family: monospace;">${s.name}</div>
                  <div style="font-size: 10px; color: #4f46e5; font-weight: 700; margin-top: 4px;">ACTIVE IN: ${s.active_branches || 'Primary Branch Only'}</div>
                </div>
                <button 
                  onclick="window.enableSupplierForBranch('${s.name}')" 
                  style="background: #4f46e5; color: #fff; border: none; padding: 6px 14px; border-radius: 8px; height: fit-content; align-self: center; font-size: 11px; font-weight: 800; cursor: pointer; transition: 0.2s;"
                  onmouseover="this.style.opacity='0.9'"
                  onmouseout="this.style.opacity='1'"
                >
                  ENABLE FOR MY BRANCH
                </button>
              </div>
            `).join('')}
          </div>
        `;

        window.enableSupplierForBranch = async (supplierName) => {
          try {
            Swal.fire({ title: 'Enabling Partner...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            const syncRes = await axios.post('/api/method/future_retail_pos.api.supplier.enable_supplier_for_branch_retail', {
              supplier: supplierName,
              warehouse: warehouse
            }, { withCredentials: true });

            if (syncRes.data.message?.success) {
              Swal.fire({ icon: 'success', title: 'Partner Enabled', text: 'Supplier is now available in your branch.', timer: 2000 });
              fetchSuppliers();
            } else {
              Swal.fire('Error', syncRes.data.message?.message || 'Failed to enable supplier.', 'error');
            }
          } catch (e) {
            Swal.fire('Error', e.message, 'error');
          }
        };

        Swal.fire({
          title: 'Global Partner Discovery',
          html: html,
          width: '600px',
          showConfirmButton: false,
          showCloseButton: true,
          customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
        });
      } else {
        Swal.fire('Not Found', 'No such supplier found in any branch.', 'info');
      }
    } catch (err) {
      console.error('Global supplier search failed:', err);
      Swal.fire('Search Failed', 'Unable to reach global registry.', 'error');
    } finally {
      setGlobalSearching(false);
    }
  };

  const total = filteredSuppliers.length;
  const paginatedSuppliers = filteredSuppliers.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(total / pageSize);

  const stats = useMemo(() => ({
    total: suppliers.length,
    active: suppliers.filter(s => !s.disabled && !s.is_frozen && !s.on_hold).length,
    inactive: suppliers.filter(s => s.disabled || s.is_frozen || s.on_hold).length,
    types: [...new Set(suppliers.map(s => s.supplier_type))].length
  }), [suppliers]);

  /* ==================== ACTIONS ==================== */
  const handleSave = async () => {
    if (!form.supplier_name.trim() || !form.supplier_group) {
      Swal.fire({ icon: 'warning', title: 'Missing Metadata', text: 'Supplier Name and Group are mandatory.' });
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form, disabled: form.disabled ? 1 : 0 };
      if (isEditMode) {
        await axios.put(`/api/resource/Supplier/${editingSupplier.name}`, payload, { withCredentials: true });
        Swal.fire({ icon: 'success', title: 'Profile Updated', timer: 2000, showConfirmButton: false });
      } else {
        await axios.post('/api/method/future_retail_pos.api.item.create_generic_doc',
          { doctype: "Supplier", data: payload }, { withCredentials: true }
        );
        Swal.fire({ icon: 'success', title: 'Partner Onboarded', timer: 2000, showConfirmButton: false });
      }
      handleCloseForm();
      fetchSuppliers();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Process Rejected', text: err.response?.data?.message || 'Transaction failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (supplier) => {
    const result = await Swal.fire({
      title: 'De-register Partner?',
      text: `Are you certain you want to purge ${supplier.supplier_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Execute Removal'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/resource/Supplier/${supplier.name}`, { withCredentials: true });
        Swal.fire({ icon: 'success', title: 'Purged' });
        fetchSuppliers();
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Access Denied' });
      }
    }
  };

  const handleEdit = (supplier) => {
    navigate(`/supplier-edit/${encodeURIComponent(supplier.name)}`);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setForm({
      supplier_name: '', supplier_group: '', supplier_type: 'Company',
      disabled: false, tax_id: '', website: '', email_id: '', mobile_no: '',
      address: '', contact_person: '', currency: 'AED'
    });
    setIsEditMode(false);
    setEditingSupplier(null);
  };



  return (
    <>
      <div className="so-page" style={{ height: 'auto', minHeight: '100vh', overflow: 'visible' }}>
        {/* Page Header */}
        <div className="so-page-header-container">
          <div className="so-page-tabs">
            <span className="so-page-tab active">Supplier</span>
            <span className="so-page-tab" onClick={() => navigate('/purchasereport')} style={{ cursor: 'pointer' }}>Reports</span>
          </div>
          <div className="so-page-header">
            <div>
              <h1 className="so-page-title">Supplier Management</h1>
              <p className="so-page-subtitle">Manage procurement and vendor records</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                onClick={toggleTheme}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.45rem 0.9rem', background: '#f8fafc',
                  border: `1.5px solid ${themeColor}`, borderRadius: '0.375rem',
                  fontSize: '0.75rem', fontWeight: 700, color: themeColor,
                  cursor: 'pointer', transition: 'all 0.2s',
                  textTransform: 'uppercase', letterSpacing: '0.04em'
                }}
              >
                <Palette size={13} />
                {isGreen ? 'BLUE' : 'GREEN'}
              </button>

              <ListCustomizer
                doctype="Supplier"
                onSave={cols => setCustomColumns(cols)}
                themeColor={themeColor}
              />

              <button className="so-btn-primary" onClick={() => navigate('/supplier-edit/new')}>
                <Plus size={16} /> Create Supplier
              </button>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="so-filter-bar">
          <div style={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column' }}>
            <label className="so-filter-label">Search Supplier</label>
            <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
              <input
                className="so-filter-input"
                type="text"
                placeholder="Name, ID or Contact..."
                value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)}
                style={{ flex: 1 }}
              />
              <button
                onClick={openGlobalSyncModal}
                disabled={globalSearching}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0 0.85rem', background: '#e0f2fe',
                  border: '1px solid #bae6fd', borderRadius: '0.375rem',
                  fontSize: '0.7rem', fontWeight: 800, color: '#0369a1',
                  cursor: 'pointer', transition: 'all 0.2s',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  height: '38px', flexShrink: 0
                }}
                title="Search and enable suppliers from other branches"
              >
                {globalSearching ? <Loader2 size={13} className="animate-spin" /> : <Globe size={13} />}
                Global Sync
              </button>
            </div>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label className="so-filter-label">Group</label>
            <select className="so-filter-input" value={filterGroup} onChange={e => { setFilterGroup(e.target.value); setCurrentPage(1); }}>
              <option value="">All Groups</option>
              {supplierGroups.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label className="so-filter-label">Entity Type</label>
            <select className="so-filter-input" value={filterType} onChange={e => { setFilterType(e.target.value); setCurrentPage(1); }}>
              <option value="">All Types</option>
              <option value="Company">Corporate / B2B</option>
              <option value="Individual">Individual / B2C</option>
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label className="so-filter-label">Status</label>
            <select className="so-filter-input" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
              <option value="">All Lifecycle States</option>
              <option value="active">Operational Only</option>
              <option value="inactive">Restricted Only</option>
            </select>
          </div>
          <button className="so-clear-btn" style={{ width: 'auto', padding: '0 1.5rem', height: '38px', margin: 0 }} onClick={() => {
            setFilterSearch(''); setFilterGroup(''); setFilterType(''); setFilterStatus('');
          }}>Reset</button>
        </div>

        {/* Executive Dashboard */}
        <div style={{ padding: '1.25rem 2rem 0' }}>
          <div className="so-summary-bar">
            <div className="so-summary-item">
              <span className="so-summary-label">Total Suppliers</span>
              <span className="so-summary-value grand">{stats.total}</span>
            </div>
            <div className="so-summary-divider" />
            <div className="so-summary-item">
              <span className="so-summary-label">Active</span>
              <span className="so-summary-value" style={{ color: '#059669' }}>{stats.active}</span>
            </div>
            <div className="so-summary-divider" />
            <div className="so-summary-item">
              <span className="so-summary-label">On Hold / Frozen</span>
              <span className="so-summary-value" style={{ color: '#ef4444' }}>{stats.inactive}</span>
            </div>
            <div className="so-summary-divider" />
            <div className="so-summary-item">
              <span className="so-summary-label">Categories</span>
              <span className="so-summary-value">{stats.types} Types</span>
            </div>
          </div>
        </div>

        {/* Main Directory Table */}
        <div style={{ padding: '1.5rem 2rem' }}>
          <div className="so-table-card">
            <div className="so-table-wrapper" style={{ maxHeight: 'none', overflowY: 'visible' }}>
              <table className="so-table">
                <thead>
                  <tr>
                    <th>Organization Profile</th>
                    <th>Contact Vectors</th>
                    <th>Classification</th>
                    {customColumns.map(col => (
                      <th key={col}>{col.replace(/_/g, ' ').toUpperCase()}</th>
                    ))}
                    <th>Status</th>
                    <th style={{ width: '120px', textAlign: 'center' }}>Controls</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5 + customColumns.length} className="so-empty">
                        <Loader2 size={28} className="so-spinner" style={{ margin: '0 auto' }} />
                      </td>
                    </tr>
                  ) : paginatedSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan={5 + customColumns.length} className="so-empty">
                        <Building2 size={36} style={{ margin: '0 auto 0.75rem', color: '#cbd5e1' }} />
                        <p>No partners match the current filter criteria.</p>
                        {filterSearch && (
                          <button
                            className="so-btn-ghost"
                            style={{ marginTop: '1rem', color: themeColor, fontWeight: 800, border: `1px solid ${themeColor}` }}
                            onClick={checkSupplierGlobally}
                            disabled={globalSearching}
                          >
                            {globalSearching ? 'Searching...' : `Check if "${filterSearch}" exists globally`}
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    paginatedSuppliers.map((s) => (
                      <tr key={s.name} onClick={() => navigate(`/supplier-details/${encodeURIComponent(s.name)}`)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{
                              width: '38px', height: '38px', borderRadius: '0.625rem',
                              background: themeLight, color: themeColor,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Building2 size={18} />
                            </div>
                            <div>
                              <div style={{ fontWeight: 800, color: 'var(--so-text-heading)', fontSize: '0.85rem' }}>{s.supplier_name}</div>
                              <div style={{ fontSize: '0.68rem', color: 'var(--so-text-muted)', fontFamily: 'monospace', fontWeight: 600 }}>{s.name}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            {(s.email_id || s.contact_details?.email_id) && (
                              <div style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#475569' }}>
                                <Mail size={12} className="text-slate-600" /> {s.email_id || s.contact_details?.email_id}
                              </div>
                            )}
                            {(s.mobile_no || s.contact_details?.mobile_no) && (
                              <div style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#475569' }}>
                                <Phone size={12} className="text-slate-600" /> {s.mobile_no || s.contact_details?.mobile_no}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--so-text-heading)' }}>{s.supplier_type}</div>
                          <div style={{ fontSize: '0.68rem', color: themeColor, textTransform: 'uppercase', fontWeight: 800, marginTop: '0.1rem' }}>{s.supplier_group}</div>
                        </td>
                        {customColumns.map(col => (
                          <td key={col} style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {s[col] !== undefined && s[col] !== null ? String(s[col]) : '-'}
                          </td>
                        ))}
                        <td>
                          <StatusBadge isInactive={s.disabled || s.is_frozen || s.on_hold} themeColor={themeColor} />
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.35rem' }}>
                            <button className="so-btn-ghost" onClick={() => navigate(`/supplier-details/${encodeURIComponent(s.name)}`)} title="View Details">
                              <Eye size={15} />
                            </button>
                            <button className="so-btn-ghost" onClick={() => handleEdit(s)} style={{ color: '#d97706' }} title="Edit Record">
                              <Edit2 size={15} />
                            </button>
                            <button className="so-btn-ghost" onClick={() => handleDelete(s)} style={{ color: '#ef4444' }} title="Remove Partner">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Grid */}
            {!loading && total > 0 && (
              <div className="so-pagination" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--so-border)', marginTop: 0 }}>
                <span style={{ fontWeight: 600 }}>Showing {Math.min((currentPage - 1) * pageSize + 1, total)}–{Math.min(currentPage * pageSize, total)} of {total} records</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.6 }}>Page Capacity:</span>
                    <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} className="so-page-btn" style={{ padding: '0.25rem 0.5rem' }}>
                      {[10, 20, 50, 100].map(sz => <option key={sz} value={sz}>{sz}</option>)}
                    </select>
                  </div>
                  <div className="so-pagination-btns" style={{ borderLeft: '1px solid var(--so-border)', paddingLeft: '1.25rem' }}>
                    <button className="so-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
                    <span style={{ fontWeight: 800, color: themeColor, padding: '0 0.75rem', fontSize: '0.8rem' }}>{currentPage} / {totalPages}</span>
                    <button className="so-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <SupplierFormModal
        isOpen={showForm}
        onClose={handleCloseForm}
        onSave={() => {
          fetchSuppliers();
          handleCloseForm();
        }}
        editingSupplier={editingSupplier}
        userWarehouse={warehouse}
      />

      {/* ────────────────────── PREMIUM GLOBAL SYNC MODAL ────────────────────── */}
      {showGlobalSyncModal && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs animate-fadeIn p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden max-h-[85vh] text-left">
            {/* Header */}
            <div className="bg-slate-50 border-b border-slate-200/80 flex items-center justify-between" style={{ padding: '1.25rem 1.75rem' }}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-sky-50 text-[#0369a1]">
                  <Globe size={18} strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-xs font-black text-slate-800 uppercase tracking-tight leading-none">Global Discovery Wizard</h3>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1.5">Sync Suppliers across branches</p>
                </div>
              </div>
              <button
                onClick={() => setShowGlobalSyncModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto flex flex-col" style={{ padding: '1.75rem', gap: '1.25rem' }}>
              {/* Search Bar inside Modal */}
              <div className="flex shrink-0" style={{ gap: '0.75rem' }}>
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search name, code, or contact globally..."
                    value={globalSyncSearch}
                    onChange={e => setGlobalSyncSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && runGlobalSearch()}
                    className="w-full border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-sky-500 transition-all bg-slate-50/50"
                    style={{ height: '2.75rem', paddingLeft: '3rem', paddingRight: '1rem' }}
                  />
                </div>
                <button
                  onClick={() => runGlobalSearch()}
                  disabled={searchingGlobal}
                  className="px-6 text-xs font-bold uppercase tracking-wider text-white bg-sky-600 rounded-xl hover:bg-sky-700 transition-all flex items-center gap-2 shadow-md cursor-pointer"
                  style={{ height: '2.75rem' }}
                >
                  {searchingGlobal ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                  Discover
                </button>
              </div>

              {/* List */}
              <div className="border border-slate-100 rounded-xl overflow-hidden bg-slate-50/30 flex-1 min-h-[250px] flex flex-col">
                {searchingGlobal ? (
                  <div className="flex-1 flex flex-col items-center justify-center p-12 text-slate-400 gap-2">
                    <Loader2 size={24} className="animate-spin text-[#0369a1]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Searching Global Registries...</span>
                  </div>
                ) : globalSuppliers.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center p-12 text-center gap-3">
                    <Building2 size={36} className="text-slate-300" />
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">No Discovered Records</p>
                      <p className="text-[10px] text-slate-400 font-medium">Type a supplier name above and click Discover</p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-y-auto max-h-[350px] divide-y divide-slate-100 bg-white">
                    {/* Select All row */}
                    <div className="bg-slate-50/50 flex items-center justify-between" style={{ padding: '0.75rem 1.25rem' }}>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedGlobalSuppliers.length === globalSuppliers.filter(s => !s.active_branches || !s.active_branches.includes(warehouse)).length && globalSuppliers.filter(s => !s.active_branches || !s.active_branches.includes(warehouse)).length > 0}
                          onChange={(e) => {
                            const unlinked = globalSuppliers.filter(s => !s.active_branches || !s.active_branches.includes(warehouse));
                            if (e.target.checked) {
                              setSelectedGlobalSuppliers(unlinked.map(s => s.name));
                            } else {
                              setSelectedGlobalSuppliers([]);
                            }
                          }}
                          className="rounded border-slate-300 text-sky-600 focus:ring-0 w-4 h-4"
                        />
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Select All Unlinked</span>
                      </label>
                      <span className="text-[9px] font-bold text-slate-400 uppercase">
                        {selectedGlobalSuppliers.length} Selected
                      </span>
                    </div>

                    {/* Records rows */}
                    {globalSuppliers.map((s, i) => {
                      const isLinked = s.active_branches && s.active_branches.includes(warehouse);
                      return (
                        <div key={i} className={`flex items-center justify-between hover:bg-slate-50/50 transition-colors ${isLinked ? 'opacity-60 bg-slate-50/20' : ''}`} style={{ padding: '0.875rem 1.25rem' }}>
                          <label className="flex items-center gap-3 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              disabled={isLinked}
                              checked={selectedGlobalSuppliers.includes(s.name)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedGlobalSuppliers(prev => [...prev, s.name]);
                                } else {
                                  setSelectedGlobalSuppliers(prev => prev.filter(name => name !== s.name));
                                }
                              }}
                              className="rounded border-slate-300 text-sky-600 focus:ring-0 w-4 h-4 disabled:opacity-50"
                            />
                            <div className="flex flex-col">
                              <span className="font-bold text-[13px] text-slate-700">{s.supplier_name}</span>
                              <span className="text-[10px] text-slate-400 font-medium font-mono">{s.name} • {s.supplier_group}</span>
                            </div>
                          </label>
                          <div className="flex items-center gap-2">
                            {isLinked ? (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100 uppercase tracking-wider">Already Linked</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-sky-50 text-sky-600 border border-sky-100 uppercase tracking-wider">Ready to Sync</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 border-t border-slate-200/80 flex items-center justify-between shrink-0" style={{ padding: '1rem 1.75rem' }}>
              <button
                onClick={() => setShowGlobalSyncModal(false)}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-rose-500 transition-all rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkSync}
                disabled={syncingGlobal || selectedGlobalSuppliers.length === 0}
                className="px-6 py-2.5 text-white bg-sky-600 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-md transition-all hover:scale-[1.02] active:scale-95 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none disabled:scale-100 cursor-pointer"
              >
                {syncingGlobal ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {syncingGlobal ? 'Syncing...' : `Sync ${selectedGlobalSuppliers.length} Selected`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}